// Components
import { useQuery } from '@apollo/client';
import {
  Button,
  Grid,
  GridItem,
  PendingContent,
  Spinner,
  Table,
} from 'plume-ui';
import { ComposedChartDataItem } from 'plume-ui/dist/utils/charts/chartTypes';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { DependencyContainer } from '../../../../DependencyContainer';
import {
  Routes,
  SHORT_DATE_FORMAT,
  SHORT_DATE_TIME_FORMAT,
  STANDARD_TIME_FORMAT,
} from '../../../../config';
import { useFeaturePermission } from '../../../../hooks/useFeaturePermission';
import { useFetchData } from '../../../../hooks/useFetchData';
import { CsvFileSummary } from '../../../../types';
import { Permission } from '../../../../userPermissionConfig';
// Hooks
import { AdjustableComponent } from '../../../../utils/hooks/useClasses';
// Helper functions
import {
  getMonitorChartGroups,
  parseMonitorDataWithOutreach,
  parseMonitorDataWithOutreachNew,
} from '../../helpers';
import {
  useGetDailyMonitorData,
  useGetMapTriggeredOutreachEvents,
  useGetOutreachStats,
} from '../../services/monitorServices';
import { Monitor, UnresolvedLocationListItem } from '../../types';

import {
  unresolvedLocationsInAlarmLimit,
  unresolvedLocationsInAlarmOffsetFromPage,
} from './fixtures';
import {
  getTimeRanges,
  getUnresolvedLocationsInAlarmHeaders,
  mapMonitorInfoCardData,
  unresolvedLocationToDataRow,
} from './helpers';
import DownloadUnresolvedListsButton from './renderers/DownloadUnresolvedListsButton';

import LeafletMap from '../LeafletMap';
import InfoCardBody from './renderers/InfoCardBody';
import InfoCardBodyGraph from './renderers/InfoCardBodyGraph';
import InfoCardBodyGrid from './renderers/InfoCardBodyGrid';

import dayjs from 'dayjs';
import { getFrontlineUrl } from '../../../../utils/environments';
import {
  getMonitorDataLatestQuery,
  getMonitorHourlyFreq,
  getMonitorOutreachStatsFreq,
  getUnresolvedLocationsMonitor,
} from '../../services/monitorQueries';
import { LeafletMapMarkerGroup } from '../LeafletMap/types';

type MonitorInfoBasicProps = {
  data: Monitor;
};

type MonitorInfoBasicStyles = {};

const { csvLocationsFileService } = new DependencyContainer();

const MonitorInfoBasic: AdjustableComponent<
  MonitorInfoBasicProps,
  MonitorInfoBasicStyles
> = ({ data }) => {
  const { t } = useTranslation();
  const { hasPermission } = useFeaturePermission();
  const { partnerId } = useParams<{ partnerId: string }>();
  const [chartDaysBack, setChartDaysBack] = useState(() => {
    if (data.frequencyInHours === 0 || data.frequencyInHours === 1) return 1;
    if (data.frequencyInHours === 3) return 3;
    return 7;
  });
  const [graphData, setGraphData] = useState<ComposedChartDataItem[]>([]);
  const [newGraphData, setNewGraphData] = useState<ComposedChartDataItem[]>([]); // new API
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [mapMarkerGroups, setMapMarkerGroups] = useState<
    LeafletMapMarkerGroup[]
  >([]);

  const [
    unresolvedLocationsInAlarmTableData,
    setUnresolvedLocationsInAlarmTableData,
  ] = useState<UnresolvedLocationListItem[]>([]);

  const {
    getMapOutreachEvents,
    triggeredMapOutreachEventsData,
    triggeredMapOutreachEventsDataLoading,
  } = useGetMapTriggeredOutreachEvents(data.id || '');

  const startDate = useMemo(
    () =>
      dayjs()
        .subtract(Math.max(data.frequencyInHours || 1, 1) * 24, 'hours')
        .utc()
        .toISOString(),
    [data.frequencyInHours],
  );

  const endDate = useMemo(() => dayjs().utc().toISOString(), []);

  const {
    data: monitorHourlyFreqData,
    loading: monitorHourlyFreqLoading,
  } = useQuery(getMonitorHourlyFreq(data.id || '', startDate, endDate), {
    skip:
      !data.id || (chartDaysBack >= 7 && (data.frequencyInHours || 1) >= 24),
  });

  const { data: monitorOutreachStats } = useQuery(
    getMonitorOutreachStatsFreq(data.id || '', partnerId, chartDaysBack >= 7),
    { skip: !data.id },
  );

  const { monitorDataLoading, monitorData } = useGetDailyMonitorData(
    data.id || '',
    chartDaysBack,
    chartDaysBack < 7,
  );

  const {
    outreachStatsDataLoading: emailsSentDataLoading,
    outreachStatsData: emailsSentData,
  } = useGetOutreachStats(data?.id ?? '', partnerId, chartDaysBack);

  const {
    loading: unresolvedLocationsInAlarmDataLoading,
    data: unresolvedLocationsInAlarmDataRaw,
  } = useQuery(
    getUnresolvedLocationsMonitor({
      monitorId: data.id || '',
      limit: unresolvedLocationsInAlarmLimit,
      offset: unresolvedLocationsInAlarmOffsetFromPage(currentPage),
    }),
    { skip: !data.id },
  );

  const unresolvedLocationsInAlarmData = useMemo(
    () =>
      unresolvedLocationsInAlarmDataRaw?.signal_3_p_evaluate_monitor_freq || [],
    [unresolvedLocationsInAlarmDataRaw],
  );

  const { data: monitorDataLatest } = useQuery(
    getMonitorDataLatestQuery(data.id || ''),
    { skip: !data.id },
  );

  const locationCount = useMemo(() => {
    if (data.frequencyInHours === 0) {
      return monitorDataLatest?.signal_3_p_evaluate_monitor_freq_aggregate
        .aggregate.count;
    }
    if (
      chartDaysBack < 7 ||
      (data.frequencyInHours && data.frequencyInHours < 24)
    ) {
      return (monitorHourlyFreqData?.signal_3_p_evaluate_monitor_hourly_freq ||
        [])[0]?.location_count; // [0] because we receive it in descending order... there's no ASC option
    }
    return monitorData?.[monitorData.length - 1]?.location_count;
  }, [monitorDataLatest, monitorHourlyFreqData, monitorData]);

  const [, csvTable, , , ,] = useFetchData<CsvFileSummary[], any>(
    async () => {
      if (!partnerId) return;
      return await csvLocationsFileService.getCsvLocationsFileList(partnerId);
    },
    undefined,
    [partnerId],
    () => partnerId === undefined,
  );

  useEffect(() => {
    if (
      !monitorDataLoading &&
      monitorData &&
      !emailsSentDataLoading &&
      emailsSentData
    ) {
      setGraphData(parseMonitorDataWithOutreach(monitorData, emailsSentData));
    }
  }, [monitorDataLoading, emailsSentDataLoading, monitorData, emailsSentData]);

  useEffect(() => {
    if (
      monitorHourlyFreqData === undefined ||
      monitorOutreachStats === undefined
    )
      return;
    setNewGraphData(
      parseMonitorDataWithOutreachNew(
        [
          ...monitorHourlyFreqData.signal_3_p_evaluate_monitor_hourly_freq,
        ].reverse(),
        monitorOutreachStats?.outreachStats || [],
      ),
    );
  }, [monitorHourlyFreqData, monitorOutreachStats]);

  useEffect(() => {
    if (
      unresolvedLocationsInAlarmData === undefined ||
      unresolvedLocationsInAlarmDataLoading
    )
      return;

    setUnresolvedLocationsInAlarmTableData((existing) => [
      ...existing,
      ...unresolvedLocationsInAlarmData,
    ]);
  }, [unresolvedLocationsInAlarmData]);

  const unresolvedListLastPage = useMemo(
    () =>
      locationCount
        ? Math.ceil(locationCount / unresolvedLocationsInAlarmLimit) - 1
        : 0,
    [locationCount, unresolvedLocationsInAlarmLimit],
  );

  useEffect(() => {
    if (triggeredMapOutreachEventsData === undefined) return;
    setMapMarkerGroups([
      {
        heatmap: true,
        heatmapConfig: {
          minOpacity: 0.3,
        },
        markers: triggeredMapOutreachEventsData.map((item) => ({
          lat: item.geoInfo.lat,
          long: item.geoInfo.long,
          tooltip: item.locationId,
          onClick: () => {
            window.open(
              `${getFrontlineUrl()}/customer/${item.customerId}/location/${
                item.locationId
              }/health`,
            );
          },
        })),
      },
    ]);
  }, [triggeredMapOutreachEventsData]);

  const handleLoadMore = () => {
    if (currentPage >= unresolvedListLastPage) return;
    setCurrentPage(currentPage + 1);
  };

  const defaultMonitorRange: number = useMemo(() => {
    switch (data.frequencyInHours) {
      case 0:
        return 1;
      case 1:
        return 1;
      case 3:
        return 3;
      case 24:
        return 7;
      default:
        return 7;
    }
  }, [data]);

  useEffect(() => {
    getMapOutreachEvents();
  }, []);

  return (
    <Grid>
      <GridItem colSpan="12" tabletColSpan="12">
        {locationCount !== undefined && (
          <LeafletMap
            isLoading={triggeredMapOutreachEventsDataLoading}
            markerCount={locationCount}
            height={500}
            markerGroups={mapMarkerGroups}
            zoomControl
            zoom={2}
            showHeatmapAtZoom={[0, 4]}
            showMarkersAtZoom={5}
          />
        )}
      </GridItem>

      <GridItem colSpan="12" tabletColSpan="12">
        <InfoCardBody
          label={t('monitors.monitorInfo.unresolvedLocations.title')}
          optionalTitle={`(${t(
            'monitors.monitorInfo.unresolvedLocations.showingOfTotal',
            {
              showing: unresolvedLocationsInAlarmTableData?.length,
              total: locationCount || 0,
            },
          )})`}
          // TODO wait for backend labelTooltip={t('monitors.monitorInfo.unresolvedLocations.tooltip')}
          classes={(current) => {
            return {
              ...current,
              infoCard: `${current.infoCard} MonitorInfo__info-card--squat`,
            };
          }}
        >
          <div className="MonitorInfo__unresolved">
            <Table
              truncateCellContent={false}
              stickyHeader
              virtual
              tableType="standard"
              tooltipLabel={t('copied')}
              headerRow={getUnresolvedLocationsInAlarmHeaders()}
              dataRows={unresolvedLocationsInAlarmTableData.map(
                unresolvedLocationToDataRow,
              )}
            />

            <PendingContent
              loading={unresolvedLocationsInAlarmDataLoading}
              loader={Spinner}
            >
              {currentPage < unresolvedListLastPage && (
                <div className="MonitorInfo__loadMoreButton">
                  <Button styleVariant="tertiary-grey" onClick={handleLoadMore}>
                    {t('monitors.monitorInfo.unresolvedLocations.loadMore')}
                  </Button>
                </div>
              )}
            </PendingContent>
            <DownloadUnresolvedListsButton
              monitor={data}
              locationCount={locationCount}
            />
          </div>
        </InfoCardBody>
      </GridItem>

      <GridItem colSpan="12" tabletColSpan="12">
        <InfoCardBodyGrid
          data={mapMonitorInfoCardData(data, locationCount, csvTable)}
          bodyProps={
            hasPermission(Permission.EditMonitor)
              ? {
                  editRoute: {
                    route: Routes.MonitorEdit,
                    params: { partnerId: partnerId, monitorId: data.id },
                  },
                }
              : {}
          }
          label={t('monitors.monitorInfo.monitorInfo')}
          loading={locationCount === undefined}
        />
      </GridItem>

      <GridItem colSpan="12" tabletColSpan="12">
        <InfoCardBodyGraph
          label={t('monitors.monitorInfo.monitorTrend.title')}
          data={chartDaysBack >= 7 ? graphData : newGraphData}
          groups={getMonitorChartGroups()}
          monitor={data}
          daysBack={chartDaysBack}
          loading={
            monitorDataLoading ||
            monitorHourlyFreqLoading ||
            emailsSentDataLoading
          }
          bodyProps={{
            onMenuItemSelect: (item) => setChartDaysBack(item.value),
            options: getTimeRanges(defaultMonitorRange),
            defaultSelected: 0,
          }}
          tickFormatter={(value, index) => {
            if (chartDaysBack >= 7) {
              const phase = Math.round(graphData.length / 6);
              if (
                index === 0 ||
                index === graphData.length - 1 ||
                index % phase === 0
              ) {
                return dayjs(value, SHORT_DATE_TIME_FORMAT).format(
                  SHORT_DATE_FORMAT,
                );
              }
            } else {
              const phase = Math.round(newGraphData.length / 6);
              if (
                index === 0 ||
                index === newGraphData.length - 1 ||
                index % phase === 0
              ) {
                return dayjs(value, SHORT_DATE_TIME_FORMAT).format(
                  STANDARD_TIME_FORMAT,
                );
              }
            }
            return '';
          }}
        />
      </GridItem>
    </Grid>
  );
};

export default memo(MonitorInfoBasic);
